import { Controller } from "@hotwired/stimulus"
import { TempFormHelper } from "~/helpers/temp_form_helper"
import { CostCalculator } from "~/helpers/costCalculator"

// This controller is used to verify the selected or currently filtered leads.
export default class extends Controller {
  static get values() {
    return {
      selectedLeadIds: { type: Array, default: [] },
      numberOfLeadsInAudience: { type: Number, default: 0 },
      campaignId: { type: Number, default: 0 },
      verifyCallsLeft: { type: Number, default: 0 },
      verifyOverageRequestsEnabled: { type: Boolean, default: false },
      unverifiedLeadsCount: { type: Number, default: 0 },
      singleCreditsBucket: { type: Boolean, default: false },
      allSelected: { type: Boolean, default: false },
      audienceVerifyUrl: { type: String, default: "" }
    }
  }

  static get targets() {
    return ["unverifiedAlert"]
  }

  // This function is triggered using stimulus actions to submit a temporary form to verify the selected or filtered
  // leads.
  submit() {
    let numberOfLeadsToVerify = 0
    const isAllSelected = this.allSelectedValue;

    let formToSubmit = TempFormHelper.create(this.audienceVerifyUrlValue, "post", (form) => {
      if (isAllSelected) {
        // Send all_selected flag for bulk verification using current filters
        TempFormHelper.addHiddenInput(form, "all_selected", "true")

        // Add current filter params to the form
        const currentParams = this.getCurrentParams()
        for (const [key, value] of Object.entries(currentParams)) {
          if (Array.isArray(value)) {
            value.forEach(v => TempFormHelper.addHiddenInput(form, key + '[]', v))
          } else {
            TempFormHelper.addHiddenInput(form, key, value)
          }
        }

        numberOfLeadsToVerify = this.numberOfLeadsInAudienceValue
      } else if (this.selectedLeadIdsValue.length > 0) {
        numberOfLeadsToVerify = this.selectedLeadIdsValue.length
        this.selectedLeadIdsValue.forEach(leadId => {
          TempFormHelper.addHiddenInput(form, "lead_ids[]", leadId)
        })
      } else {
        numberOfLeadsToVerify = this.numberOfLeadsInAudienceValue
        TempFormHelper.addHiddenInput(form, "campaign_id", this.campaignIdValue)
      }
    })

    const verificationCost = CostCalculator.calculate("verify", numberOfLeadsToVerify, this.singleCreditsBucketValue)
    if (verificationCost > this.verifyCallsLeftValue && this.verifyOverageRequestsEnabledValue) {
      // Show dialog informing user about usage of overage requests.
      // If user confirms, submit the form.
      formToSubmit.dataset.confirmDialogId = "confirm-overage-usage"
      formToSubmit.dataset.turboConfirm = i18n.t("js.back.lead_actions.overage_verify_usage")
    }

    formToSubmit.addEventListener("turbo:submit-end", () => { this.verifyCallsLeftValue -= verificationCost })
    formToSubmit.requestSubmit()
  }

  verifyUnverifiedLeads() {
    let numberOfLeadsToVerify = this.unverifiedLeadsCountValue
    let formToSubmit = TempFormHelper.create(this.audienceVerifyUrlValue, "post", (form) => {
      TempFormHelper.addHiddenInput(form, "campaign_id", this.campaignIdValue)
      TempFormHelper.addHiddenInput(form, "only_unverified", "true")
    })

    const verificationCost = CostCalculator.calculate("verify", numberOfLeadsToVerify, this.singleCreditsBucketValue)
    if (verificationCost > this.verifyCallsLeftValue && this.verifyOverageRequestsEnabledValue) {
      // Show dialog informing user about usage of overage requests.
      // If user confirms, submit the form.
      formToSubmit.dataset.confirmDialogId = "confirm-overage-usage"
      formToSubmit.dataset.turboConfirm = i18n.t("js.back.lead_actions.overage_verify_usage")
    }

    formToSubmit.addEventListener("turbo:submit-end", () => {
      this.verifyCallsLeftValue -= verificationCost
      this.unverifiedAlertTarget.remove()
    })
    formToSubmit.requestSubmit()
  }

  // This function is triggered using stimulus actions to update the selected leads value.
  updateSelection(event) {
    if (event.detail) {
      // Store whether all items are selected
      this.allSelectedValue = !!event.detail.allSelected;

      if (event.detail.dataSets) {
        this.selectedLeadIdsValue = event.detail.dataSets.map(dataSet => dataSet.leadId).filter(Boolean)
      } else {
        this.selectedLeadIdsValue = []
      }
    } else {
      this.allSelectedValue = false;
      this.selectedLeadIdsValue = []
    }
  }

  // Helper method to get current filter parameters from the URL
  getCurrentParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const params = {};

    for (const [key, value] of urlParams.entries()) {
      // Handle array parameters (with [] notation)
      if (key.endsWith('[]')) {
        const baseKey = key.slice(0, -2);
        if (!params[baseKey]) params[baseKey] = [];
        params[baseKey].push(value);
      } else {
        params[key] = value;
      }
    }

    return params;
  }
}
